// Servizio Email con Resend - Completamente Gratuito e Opensource
// https://resend.com - 3.000 email/mese gratuite
import { getFunctions, httpsCallable } from 'firebase/functions';

export interface ReminderEmailData {
  to_name: string;
  to_email: string;
  company_name: string;
  position: string;
  applied_date: string;
  days_since_application: number;
  application_url?: string;
}

export interface WelcomeEmailData {
  to_name: string;
  to_email: string;
  user_id: string;
  signup_date: string;
}

export interface InterviewReminderData {
  to_name: string;
  to_email: string;
  company_name: string;
  position: string;
  interview_date: string;
  interview_time: string;
  interview_location?: string;
  interview_type: string;
}

export interface WeeklyDigestData {
  to_name: string;
  to_email: string;
  week_start: string;
  week_end: string;
  total_applications: number;
  new_applications: number;
  interviews_scheduled: number;
  responses_received: number;
  applications_url: string;
}

export type EmailType = 'reminder' | 'welcome' | 'interview' | 'digest' | 'test';

class ResendEmailService {
  apiKey: string = '';
  fromEmail: string = 'noreply@job.studioquovadis.ch';
  isConfigured: boolean = false;

  constructor() {
    this.initializeService();
  }

  initializeService(): void {
    // Prova a prendere l'API key da localStorage (per admin)
    const savedApiKey = localStorage.getItem('resend_api_key');
    if (savedApiKey) {
      this.apiKey = savedApiKey;
      this.isConfigured = true;
      console.log('✅ Resend configurato con API key salvata');
      console.log('📧 Email di invio:', this.fromEmail);
    } else {
      console.log('⚠️ Resend non configurato - API key mancante');
    }
  }

  configure(apiKey: string, fromEmail?: string): void {
    this.apiKey = apiKey;
    if (fromEmail) {
      this.fromEmail = fromEmail;
    }
    this.isConfigured = true;
    
    // Salva in localStorage per persistenza
    localStorage.setItem('resend_api_key', apiKey);
    
    console.log('✅ Resend configurato con successo');
    console.log('📧 Email di invio:', this.fromEmail);
  }

  async verifyConfiguration(): Promise<{
    configured: boolean;
    apiKey: string;
    fromEmail: string;
    testResult?: boolean;
    error?: string;
  }> {
    if (!this.isConfigured) {
      return {
        configured: false,
        apiKey: '',
        fromEmail: this.fromEmail,
        error: 'API key non configurata'
      };
    }

    try {
      // Test di invio email
      const testResult = await this.sendCustomTestEmail('test@example.com', 'Test User');
      
      return {
        configured: true,
        apiKey: this.apiKey.substring(0, 10) + '...',
        fromEmail: this.fromEmail,
        testResult
      };
    } catch (error) {
      return {
        configured: true,
        apiKey: this.apiKey.substring(0, 10) + '...',
        fromEmail: this.fromEmail,
        testResult: false,
        error: error instanceof Error ? error.message : 'Errore sconosciuto'
      };
    }
  }

  async sendEmail(to: string, subject: string, htmlContent: string): Promise<boolean> {
    if (!this.isConfigured) {
      console.error('❌ Resend non configurato');
      return false;
    }

    try {
      const functions = getFunctions();
      const sendEmailFunction = httpsCallable(functions, 'sendEmail');
      
      const result = await sendEmailFunction({
        to,
        subject,
        html: htmlContent,
        from: this.fromEmail
      });

      console.log('✅ Email inviata con successo:', result);
      return true;
    } catch (error) {
      console.error('❌ Errore invio email:', error);
      return false;
    }
  }

  generateReminderTemplate(data: ReminderEmailData): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Reminder Candidatura - Job Tracker</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #1f2937;
            background-color: #f9fafb;
            padding: 20px;
          }
          .container {
            max-width: 600px;
            margin: 0 auto;
            background-color: #ffffff;
            border-radius: 12px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            overflow: hidden;
          }
          .header {
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            color: white;
            padding: 40px 30px;
            text-align: center;
          }
          .logo {
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 10px;
            letter-spacing: -0.5px;
          }
          .title {
            font-size: 24px;
            font-weight: 600;
            opacity: 0.95;
          }
          .content {
            padding: 40px 30px;
          }
          .greeting {
            font-size: 18px;
            margin-bottom: 25px;
            color: #374151;
          }
          .highlight {
            background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
            padding: 25px;
            border-radius: 12px;
            border-left: 5px solid #2563eb;
            margin: 25px 0;
            box-shadow: 0 2px 4px rgba(37, 99, 235, 0.1);
          }
          .company-info {
            background-color: #f8fafc;
            padding: 25px;
            border-radius: 12px;
            margin: 25px 0;
            border: 1px solid #e2e8f0;
          }
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin-top: 15px;
          }
          .info-item {
            padding: 12px;
            background-color: white;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
          }
          .info-label {
            font-size: 12px;
            color: #6b7280;
            text-transform: uppercase;
            font-weight: 600;
            letter-spacing: 0.5px;
            margin-bottom: 4px;
          }
          .info-value {
            font-size: 14px;
            font-weight: 600;
            color: #1f2937;
          }
          .cta-button {
            display: inline-block;
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            color: white;
            padding: 16px 32px;
            text-decoration: none;
            border-radius: 8px;
            font-weight: 600;
            margin: 25px 0;
            text-align: center;
            box-shadow: 0 4px 6px rgba(37, 99, 235, 0.25);
            transition: all 0.2s ease;
          }
          .cta-button:hover {
            transform: translateY(-1px);
            box-shadow: 0 6px 12px rgba(37, 99, 235, 0.3);
          }
          .footer {
            background-color: #f8fafc;
            text-align: center;
            padding: 30px;
            border-top: 1px solid #e5e7eb;
            color: #6b7280;
            font-size: 14px;
          }
          .footer a {
            color: #2563eb;
            text-decoration: none;
            font-weight: 500;
          }
          .footer a:hover {
            text-decoration: underline;
          }
          .stats {
            display: flex;
            justify-content: space-around;
            margin: 25px 0;
            padding: 20px;
            background-color: #f0f9ff;
            border-radius: 12px;
            border: 1px solid #bae6fd;
          }
          .stat-item {
            text-align: center;
          }
          .stat-number {
            font-size: 24px;
            font-weight: 700;
            color: #0369a1;
            display: block;
          }
          .stat-label {
            font-size: 12px;
            color: #0c4a6e;
            text-transform: uppercase;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
          @media (max-width: 600px) {
            .info-grid {
              grid-template-columns: 1fr;
            }
            .stats {
              flex-direction: column;
              gap: 15px;
            }
            .content {
              padding: 25px 20px;
            }
            .header {
              padding: 30px 20px;
            }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="logo">🎯 Job Tracker</div>
            <h1 class="title">Reminder Candidatura</h1>
          </div>
          
          <div class="content">
            <p class="greeting">Ciao <strong>${data.to_name}</strong>,</p>
            
            <p>Hai candidato per <strong>${data.company_name}</strong> come <strong>${data.position}</strong> 
            il <strong>${data.applied_date}</strong> (${data.days_since_application} giorni fa).</p>
            
            <div class="highlight">
              <p style="font-size: 16px; font-weight: 600; margin-bottom: 10px;">⏰ Non hai ancora ricevuto risposta</p>
              <p>Potrebbe essere il momento di fare un follow-up per mostrare il tuo interesse e mantenere viva la candidatura!</p>
            </div>
            
            <div class="company-info">
              <h3 style="margin-bottom: 15px; color: #1f2937;">📋 Dettagli Candidatura</h3>
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">Azienda</div>
                  <div class="info-value">${data.company_name}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Posizione</div>
                  <div class="info-value">${data.position}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Data Candidatura</div>
                  <div class="info-value">${data.applied_date}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Giorni Trascorsi</div>
                  <div class="info-value">${data.days_since_application}</div>
                </div>
              </div>
            </div>
            
            <div class="stats">
              <div class="stat-item">
                <span class="stat-number">${data.days_since_application}</span>
                <span class="stat-label">Giorni</span>
              </div>
              <div class="stat-item">
                <span class="stat-number">⏳</span>
                <span class="stat-label">In Attesa</span>
              </div>
              <div class="stat-item">
                <span class="stat-number">📧</span>
                <span class="stat-label">Follow-up</span>
              </div>
            </div>
              
            ${data.application_url ? `
            <a href="${data.application_url}" class="cta-button">
              👁️ Vedi Candidatura
            </a>
            ` : ''}
            
            <p style="margin-top: 25px; font-style: italic; color: #6b7280;">
              💡 <strong>Suggerimento:</strong> Un follow-up professionale può aumentare le tue possibilità di ricevere una risposta!
            </p>
          </div>
          
          <div class="footer">
            <p>🚀 Questo è un reminder automatico di <strong>Job Tracker</strong></p>
            <p>📅 Data reminder: ${new Date().toLocaleDateString('it-IT')}</p>
            <p style="margin-top: 15px;">
              <a href="https://job.studioquovadis.ch/settings">
                ⚙️ Gestisci le tue notifiche
              </a>
            </p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  generateInterviewTemplate(data: InterviewReminderData): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Reminder Colloquio - Job Tracker</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #1f2937;
            background-color: #f9fafb;
            padding: 20px;
          }
          .container {
            max-width: 600px;
            margin: 0 auto;
            background-color: #ffffff;
            border-radius: 12px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            overflow: hidden;
          }
          .header {
            background: linear-gradient(135deg, #059669 0%, #047857 100%);
            color: white;
            padding: 40px 30px;
            text-align: center;
          }
          .logo {
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 10px;
            letter-spacing: -0.5px;
          }
          .title {
            font-size: 24px;
            font-weight: 600;
            opacity: 0.95;
          }
          .content {
            padding: 40px 30px;
          }
          .greeting {
            font-size: 18px;
            margin-bottom: 25px;
            color: #374151;
          }
          .highlight {
            background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
            padding: 25px;
            border-radius: 12px;
            border-left: 5px solid #059669;
            margin: 25px 0;
            box-shadow: 0 2px 4px rgba(5, 150, 105, 0.1);
          }
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin: 25px 0;
          }
          .info-item {
            padding: 15px;
            background-color: #f8fafc;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
          }
          .info-label {
            font-size: 12px;
            color: #6b7280;
            text-transform: uppercase;
            font-weight: 600;
            letter-spacing: 0.5px;
            margin-bottom: 4px;
          }
          .info-value {
            font-size: 14px;
            font-weight: 600;
            color: #1f2937;
          }
          .tips {
            background-color: #fef3c7;
            padding: 20px;
            border-radius: 12px;
            margin: 25px 0;
            border: 1px solid #f59e0b;
          }
          .tips h3 {
            color: #92400e;
            margin-bottom: 15px;
            font-size: 16px;
          }
          .tips ul {
            color: #92400e;
            padding-left: 20px;
          }
          .tips li {
            margin-bottom: 8px;
          }
          .footer {
            background-color: #f8fafc;
            text-align: center;
            padding: 30px;
            border-top: 1px solid #e5e7eb;
            color: #6b7280;
            font-size: 14px;
          }
          @media (max-width: 600px) {
            .info-grid {
              grid-template-columns: 1fr;
            }
            .content {
              padding: 25px 20px;
            }
            .header {
              padding: 30px 20px;
            }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="logo">🎯 Job Tracker</div>
            <h1 class="title">Reminder Colloquio</h1>
          </div>
          
          <div class="content">
            <p class="greeting">Ciao <strong>${data.to_name}</strong>,</p>
            
            <p>Hai un colloquio programmato per <strong>${data.company_name}</strong> come <strong>${data.position}</strong>.</p>
            
            <div class="highlight">
              <p style="font-size: 16px; font-weight: 600; margin-bottom: 10px;">📅 Colloquio in programma</p>
              <p>Assicurati di essere preparato e puntuale!</p>
            </div>
            
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Azienda</div>
                <div class="info-value">${data.company_name}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Posizione</div>
                <div class="info-value">${data.position}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Data</div>
                <div class="info-value">${data.interview_date}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Ora</div>
                <div class="info-value">${data.interview_time}</div>
              </div>
              ${data.interview_location ? `
              <div class="info-item" style="grid-column: 1 / -1;">
                <div class="info-label">Luogo</div>
                <div class="info-value">${data.interview_location}</div>
              </div>
              ` : ''}
              <div class="info-item">
                <div class="info-label">Tipo</div>
                <div class="info-value">${data.interview_type}</div>
              </div>
            </div>
            
            <div class="tips">
              <h3>💡 Suggerimenti per il colloquio:</h3>
              <ul>
                <li>Prepara domande sull'azienda e la posizione</li>
                <li>Rivedi il tuo CV e le esperienze rilevanti</li>
                <li>Arriva con 10-15 minuti di anticipo</li>
                <li>Porta copie del CV e documenti necessari</li>
                <li>Vestiti in modo professionale e appropriato</li>
              </ul>
            </div>
            
            <p style="margin-top: 25px; font-style: italic; color: #6b7280;">
              🍀 <strong>Buona fortuna!</strong> Crediamo in te!
            </p>
          </div>
          
          <div class="footer">
            <p>🚀 Questo è un reminder automatico di <strong>Job Tracker</strong></p>
            <p>📅 Data reminder: ${new Date().toLocaleDateString('it-IT')}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  generateDigestTemplate(data: WeeklyDigestData): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Digest Settimanale</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; }
          .container { background-color: #f8f9fa; padding: 30px; border-radius: 10px; }
          .header { text-align: center; margin-bottom: 30px; }
          .logo { font-size: 24px; font-weight: bold; color: #2563eb; margin-bottom: 10px; }
          .content { margin-bottom: 25px; }
          .stats { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin: 20px 0; }
          .stat-card { background-color: white; padding: 15px; border-radius: 8px; text-align: center; }
          .stat-number { font-size: 24px; font-weight: bold; color: #2563eb; }
          .stat-label { font-size: 14px; color: #6b7280; }
          .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 14px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="logo">Job Tracker</div>
            <h1 class="title">Digest Settimanale</h1>
            <p>${data.week_start} - ${data.week_end}</p>
          </div>
          
          <div class="content">
            <p>Ciao <strong>${data.to_name}</strong>,</p>
            
            <p>Ecco il riassunto della tua settimana di ricerca lavoro:</p>
            
            <div class="stats">
              <div class="stat-card">
                <div class="stat-number">${data.total_applications}</div>
                <div class="stat-label">Candidature Totali</div>
              </div>
              <div class="stat-card">
                <div class="stat-number">${data.new_applications}</div>
                <div class="stat-label">Nuove Candidature</div>
              </div>
              <div class="stat-card">
                <div class="stat-number">${data.interviews_scheduled}</div>
                <div class="stat-label">Colloqui Programmati</div>
              </div>
              <div class="stat-card">
                <div class="stat-number">${data.responses_received}</div>
                <div class="stat-label">Risposte Ricevute</div>
              </div>
            </div>
            
            <p>Continua così! La perseveranza è la chiave del successo.</p>
          </div>
          
          <div class="footer">
            <p>Questo è un digest automatico di Job Tracker</p>
            <p>Data: ${new Date().toLocaleDateString('it-IT')}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  async sendApplicationReminder(data: ReminderEmailData): Promise<boolean> {
    console.log('📧 Invio reminder candidatura con Resend...');
    
    const subject = `Reminder: Candidatura ${data.company_name} - ${data.position}`;
    const htmlContent = this.generateReminderTemplate(data);
    
    return this.sendEmail(data.to_email, subject, htmlContent);
  }

  async sendInterviewReminder(data: InterviewReminderData): Promise<boolean> {
    console.log('📧 Invio reminder colloquio con Resend...');
    
    const subject = `Reminder Colloquio: ${data.company_name} - ${data.position}`;
    const htmlContent = this.generateInterviewTemplate(data);
    
    return this.sendEmail(data.to_email, subject, htmlContent);
  }

  async sendWeeklyDigest(data: WeeklyDigestData): Promise<boolean> {
    console.log('📧 Invio digest settimanale con Resend...');
    
    const subject = `Digest Settimanale Job Tracker - ${data.week_start}`;
    const htmlContent = this.generateDigestTemplate(data);
    
    return this.sendEmail(data.to_email, subject, htmlContent);
  }

  async sendTestEmail(userEmail: string, userName: string): Promise<boolean> {
    console.log('📧 Invio email di test con Resend...');
    
    const testData: ReminderEmailData = {
      to_name: userName,
      to_email: userEmail,
      company_name: 'Azienda Test',
      position: 'Posizione Test',
      applied_date: new Date().toLocaleDateString('it-IT'),
      days_since_application: 7,
      application_url: 'https://job.studioquovadis.ch'
    };
    
    return this.sendApplicationReminder(testData);
  }

  async sendCustomTestEmail(toEmail: string, toName: string): Promise<boolean> {
    console.log('📧 Invio email di test personalizzata...');
    
    const subject = '🎯 Test Template Email - Job Tracker';
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Test Template Email - Job Tracker</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #1f2937;
            background-color: #f9fafb;
            padding: 20px;
          }
          .container {
            max-width: 600px;
            margin: 0 auto;
            background-color: #ffffff;
            border-radius: 12px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            overflow: hidden;
          }
          .header {
            background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
            color: white;
            padding: 40px 30px;
            text-align: center;
          }
          .logo {
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 10px;
            letter-spacing: -0.5px;
          }
          .title {
            font-size: 24px;
            font-weight: 600;
            opacity: 0.95;
          }
          .content {
            padding: 40px 30px;
          }
          .greeting {
            font-size: 18px;
            margin-bottom: 25px;
            color: #374151;
          }
          .highlight {
            background: linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%);
            padding: 25px;
            border-radius: 12px;
            border-left: 5px solid #7c3aed;
            margin: 25px 0;
            box-shadow: 0 2px 4px rgba(124, 58, 237, 0.1);
          }
          .template-showcase {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin: 25px 0;
          }
          .template-card {
            background-color: #f8fafc;
            padding: 20px;
            border-radius: 12px;
            border: 1px solid #e5e7eb;
            text-align: center;
          }
          .template-icon {
            font-size: 24px;
            margin-bottom: 10px;
          }
          .template-title {
            font-weight: 600;
            color: #1f2937;
            margin-bottom: 5px;
          }
          .template-desc {
            font-size: 12px;
            color: #6b7280;
          }
          .footer {
            background-color: #f8fafc;
            text-align: center;
            padding: 30px;
            border-top: 1px solid #e5e7eb;
            color: #6b7280;
            font-size: 14px;
          }
          .footer a {
            color: #7c3aed;
            text-decoration: none;
            font-weight: 500;
          }
          .footer a:hover {
            text-decoration: underline;
          }
          @media (max-width: 600px) {
            .template-showcase {
              grid-template-columns: 1fr;
            }
            .content {
              padding: 25px 20px;
            }
            .header {
              padding: 30px 20px;
            }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="logo">🎯 Job Tracker</div>
            <h1 class="title">Test Template Email</h1>
          </div>
          
          <div class="content">
            <p class="greeting">Ciao <strong>${toName}</strong>,</p>
            
            <p>Questa è una email di test per verificare che i template HTML di Job Tracker funzionino correttamente!</p>
            
            <div class="highlight">
              <p style="font-size: 16px; font-weight: 600; margin-bottom: 10px;">✅ Template Email Configurati</p>
              <p>I seguenti template sono ora disponibili e completamente funzionali:</p>
            </div>
            
            <div class="template-showcase">
              <div class="template-card">
                <div class="template-icon">📧</div>
                <div class="template-title">Reminder Candidature</div>
                <div class="template-desc">Email automatica dopo 7 giorni senza risposta</div>
              </div>
              <div class="template-card">
                <div class="template-icon">📅</div>
                <div class="template-title">Reminder Colloqui</div>
                <div class="template-desc">Promemoria 24h e 1h prima del colloquio</div>
              </div>
              <div class="template-card">
                <div class="template-icon">📊</div>
                <div class="template-title">Digest Settimanale</div>
                <div class="template-desc">Riepilogo settimanale delle candidature</div>
              </div>
              <div class="template-card">
                <div class="template-icon">🎨</div>
                <div class="template-title">Design Responsive</div>
                <div class="template-desc">Ottimizzato per desktop e mobile</div>
              </div>
            </div>
            
            <p style="margin-top: 25px; font-style: italic; color: #6b7280;">
              🚀 <strong>Job Tracker</strong> è ora completamente configurato con Resend per l'invio di email professionali!
            </p>
            
            <p style="margin-top: 15px; color: #059669; font-weight: 600;">
              ✅ Migrazione da EmailJS a Resend completata con successo
            </p>
          </div>
          
          <div class="footer">
            <p>🚀 Questo è un test automatico di <strong>Job Tracker</strong></p>
            <p>📅 Data test: ${new Date().toLocaleDateString('it-IT')} ${new Date().toLocaleTimeString('it-IT')}</p>
            <p style="margin-top: 15px;">
              <a href="https://job.studioquovadis.ch">
                🌐 Visita Job Tracker
              </a>
            </p>
          </div>
        </div>
      </body>
      </html>
    `;
    
    return this.sendEmail(toEmail, subject, htmlContent);
  }

  async sendEmailByType(type: EmailType, data: any): Promise<boolean> {
    switch (type) {
      case 'reminder':
        return this.sendApplicationReminder(data);
      case 'interview':
        return this.sendInterviewReminder(data);
      case 'digest':
        return this.sendWeeklyDigest(data);
      case 'test':
        return this.sendTestEmail(data.email, data.name);
      default:
        console.error('❌ Tipo email non supportato:', type);
        return false;
    }
  }

  isServiceConfigured(): boolean {
    return this.isConfigured;
  }

  getConfig() {
    return {
      configured: this.isConfigured,
      apiKey: this.apiKey ? this.apiKey.substring(0, 10) + '...' : 'Non configurata',
      fromEmail: this.fromEmail,
      provider: 'Resend'
    };
  }

  resetConfiguration(): void {
    this.apiKey = '';
    this.isConfigured = false;
    localStorage.removeItem('resend_api_key');
    console.log('🔄 Configurazione Resend resettata');
  }
}

export const resendEmailService = new ResendEmailService(); 